import { store } from '@/services/store.service'
import NoteCard from '@/components/NoteCard'
import { NoteCardLoadingSkeleton } from '@/components/NoteCard'
import PrimaryPageLayout, { TPrimaryPageLayoutRef } from '@/layouts/PrimaryPageLayout'
import { Button } from '@/components/ui/button'
import { Event } from '@nostr/tools/wasm'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { onEventRemoved } from '@/services/client.service'
import { useAtomValue } from 'jotai'
import { currentPubkeyAtom } from '@/atoms/session'
import { parseFilter } from '@/lib/filter-parser'
import { HugeiconsIcon } from '@hugeicons/react'
import { DatabaseSearchIcon } from '@hugeicons/core-free-icons'
import { getErrorMessage } from '@/lib/error'

const DatabasePage = forwardRef((_, ref) => {
  const { t } = useTranslation()
  const currentPubkey = useAtomValue(currentPubkeyAtom)
  const [input, setInput] = useState(window.localStorage.getItem('s:databaseInput') || '')
  const [events, setEvents] = useState<Event[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set())
  const layoutRef = useRef<TPrimaryPageLayoutRef>(null)

  useImperativeHandle(
    ref,
    () => ({
      scrollToTop: (behavior: ScrollBehavior = 'smooth') => layoutRef.current?.scrollToTop(behavior)
    }),
    []
  )

  useEffect(() => {
    function handler(eventId: string) {
      setRemovingIds((prev) => new Set(prev).add(eventId))

      setTimeout(() => {
        setEvents((events) => (events ? events.filter((e) => e.id !== eventId) : null))
        setRemovingIds((prev) => {
          const next = new Set(prev)
          next.delete(eventId)
          return next
        })
      }, 300)
    }

    return onEventRemoved(handler)
  }, [])

  return (
    <PrimaryPageLayout
      ref={layoutRef}
      pageName="database"
      titlebar={
        <div className="flex items-center gap-2 px-3 h-full">
          <HugeiconsIcon icon={DatabaseSearchIcon} />
          <div className="text-lg font-semibold truncate">{t('Database Inspector')}</div>
        </div>
      }
      displayScrollToTopButton
    >
      <div className="flex items-center gap-2 px-4 pt-2">
        <div className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors md:text-sm [&:has(:focus-visible)]:ring-ring [&:has(:focus-visible)]:ring-1 [&:has(:focus-visible)]:outline-none">
          <input
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              localStorage.setItem('s:databaseInput', e.target.value)
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') runQuery()
            }}
            placeholder="kind:1 kind:9802 author:dergigi.com"
            className="size-full border-none bg-transparent focus:outline-none placeholder:text-muted-foreground"
          />
        </div>
        <Button size="sm" onClick={runQuery} disabled={loading || !input.trim()}>
          {t('Query')}
        </Button>
      </div>
      <div className="px-4 pb-2">
        <p className="text-xs text-muted-foreground">
          {t('Supported keys: kind, author, id, limit, since, until, #{tag}')}
        </p>
      </div>

      {error && (
        <div className="px-4 py-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {loading && (
        <div>
          <NoteCardLoadingSkeleton />
          <NoteCardLoadingSkeleton />
          <NoteCardLoadingSkeleton />
        </div>
      )}

      {!loading && events && events.length === 0 && (
        <div className="px-4 py-3">
          <p className="text-sm text-muted-foreground">{t('No events in the local database.')}</p>
        </div>
      )}

      {!loading &&
        events &&
        events.map((event) => (
          <div
            key={event.id}
            className={'transition-all duration-300 ease-out'.concat(
              removingIds.has(event.id) ? ' opacity-0 scale-95' : ''
            )}
          >
            <NoteCard event={event} />
          </div>
        ))}
    </PrimaryPageLayout>
  )

  async function runQuery() {
    const filter = await parseFilter(input, currentPubkey ?? undefined)
    if (!filter) {
      setError(t('Invalid filter. Use key:value pairs like "kind:1 author:<pubkey>"'))
      setEvents(null)
      return
    }

    if (!filter.limit) filter.limit = 20

    setError(null)
    setLoading(true)
    try {
      const result = await store.queryEvents(filter)
      setEvents(result)
    } catch (e) {
      setError(getErrorMessage(e))
      setEvents(null)
    } finally {
      setLoading(false)
    }
  }
})
DatabasePage.displayName = 'DatabasePage'
export default DatabasePage
